import { Button, Spinner } from '@heroui/react';
import { CopyableField } from '@/components/common/CopyableField';
import { StatusChip } from '@/components/common/StatusChip';
import { useWebmailStatus } from '@/features/domains/hooks';
import type { DelegationState } from '@/features/domains/types';
import { ArrowPathIcon, InboxIcon } from '@/icons';

const STATE = {
  'dns-pending': { tone: 'warning', label: 'DNS not pointing to Nifty' },
  'certificate-pending': { tone: 'info', label: 'Waiting for HTTPS certificate' },
  ready: { tone: 'success', label: 'HTTPS ready' },
} as const;

/** Branded webmail (https://mail.<domain>) and what is still needed before it works. */
export function WebmailCard({ zoneId, delegation, nameservers = [] }: { zoneId: string; delegation?: DelegationState; nameservers?: string[] }) {
  const status = useWebmailStatus(zoneId);
  const s = status.data;
  const externalDns = delegation !== 'nifty';

  return (
    <div className="rounded-xl border border-border bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <InboxIcon className="h-5 w-5 text-fg-muted" />
          <h3 className="text-sm font-semibold text-fg">Branded webmail</h3>
          {s && <StatusChip tone={STATE[s.state].tone}>{STATE[s.state].label}</StatusChip>}
        </div>
        <Button size="sm" variant="light" startContent={<ArrowPathIcon className="h-4 w-4" />}
          isLoading={status.isFetching} onPress={() => status.refetch()}>Check again</Button>
      </div>

      {status.isLoading && <div className="flex justify-center py-6"><Spinner size="sm" label="Checking DNS and HTTPS…" /></div>}
      {status.isError && <p className="mt-3 text-sm text-danger">Webmail status could not be checked.</p>}
      {s && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-fg">
            {s.state === 'ready'
              ? <a className="font-mono text-primary underline-offset-2 hover:underline" href={s.url} target="_blank" rel="noopener noreferrer">{s.url}</a>
              : <span className="font-mono">{s.url}</span>}
            <span className="ml-2 text-xs text-fg-muted">Users sign in with their full mailbox address and password.</span>
          </p>
          <ul className="space-y-1 text-xs text-fg-muted">
            <li><span className="font-medium text-fg">DNS:</span> {s.dns.detail}</li>
            <li>
              <span className="font-medium text-fg">HTTPS:</span>{' '}
              {!s.https.checked ? 'checked once DNS points to Nifty.'
                : s.https.valid ? `valid certificate${s.https.issuer ? ` from ${s.https.issuer}` : ''}${s.https.validTo ? `, expires ${new Date(s.https.validTo).toLocaleDateString()}` : ''}.`
                  : 'no valid certificate for this name yet. Nifty issues it after DNS points here.'}
            </li>
          </ul>
          {s.alternatives.length > 0 && (
            <p className="text-xs text-fg-muted">
              {s.alternatives.map((a) => `${a.hostname} is already used (${a.inUseBy})`).join('; ')}, so {s.hostname} is used instead.
            </p>
          )}
          {s.state === 'dns-pending' && (
            <div className="rounded-lg border border-warning/25 bg-warning/5 p-3">
              <p className="text-sm font-medium text-fg">
                {externalDns ? 'Add this record at your current DNS host' : 'Add this record to the zone, then publish'}
              </p>
              {externalDns && s.zone?.pointsToNifty && (
                <p className="mt-1 text-xs text-fg-muted">
                  This Nifty zone already has the record{s.zone.published ? '' : ' (not yet published)'}, but the internet asks
                  {nameservers.length ? <> <span className="font-mono">{nameservers.join(', ')}</span></> : ' your current DNS host'} for
                  this domain, so the record must be added there too.
                </p>
              )}
              <div className="mt-2 grid gap-2 sm:grid-cols-[6rem_1fr_1fr_5rem]">
                <CopyableField label="Type" value={s.record.type} />
                <CopyableField label="Name" value={s.record.name} />
                <CopyableField label="Value" value={s.record.value} />
                <CopyableField label="TTL" value={String(s.record.ttl)} />
              </div>
              <p className="mt-2 text-xs text-fg-muted">
                Or a CNAME: <span className="font-mono">{s.alternative.name}</span> → <span className="font-mono">{s.alternative.value}</span>.
                {' '}An explicit record overrides a wildcard (*) record. Leave MX and other mail records unchanged.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
